// The Grid view (prototype `gridView`): one deciding column down the rows,
// another across, a page selector for a third. Pick a set and PAINT cells —
// click or drag — or switch a whole row / column from its heading; without a
// set picked, a cell opens the list of sets asked there. The page of rows and
// columns (and the rows with gaps) comes from the server's `/coverage`; a
// cell's sets are worked out with the shared rule algebra as you paint, so the
// grid answers at once. Every edit is ordinary rule algebra (gridOps.ts).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeftRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { tw } from '../../testHooks'
import type { UdaCoverage } from '../types'
import { GRID_ROW_PAGE, GRID_VIEW_COLS, GRID_VIEW_ROW_STEP } from '../limits'
import { cellSets, gridAxes, setCovers } from '../rules'
import { driverLabel, formOf, tenantBodyOf } from './model'
import { cellCombo, paintCell, toggleSlice, type GridAxes } from './gridOps'
import { localCoverage } from './coverage'
import { opSet, useSetup } from './state'
import { useValuesByDriver } from './StepCombinations'
import { SetDot } from './ui'

export default function GridView({ focus }: { focus: { q?: string; onlyGaps?: boolean; nonce: number } | null }) {
  const { formNo, draft, mode, transport, objName, labelOf, domains, openDialog, notify } = useSetup()
  const form = formOf(draft.layer, formNo)
  const drivers = form?.drivers || []
  const { values, ready } = useValuesByDriver(formNo)
  const lab = labelOf(formNo)

  const auto = useMemo(() => gridAxes(drivers, values), [drivers, values])
  const [override, setOverride] = useState<{ rows: string; cols: string | null } | null>(null)
  const axes: GridAxes | null = useMemo(() => {
    const keys = drivers.map(d => d.key)
    if (!keys.length || !auto.rows) return null
    let rows = auto.rows
    let cols = auto.cols
    if (override && keys.includes(override.rows) && (override.cols === null || keys.includes(override.cols)) && override.rows !== override.cols) {
      rows = override.rows
      cols = keys.length > 1 ? override.cols : null
    }
    const page = keys.find(k => k !== rows && k !== cols) || null
    return { rows, cols, page }
  }, [drivers, auto, override])

  const [pageValue, setPageValue] = useState<string | null>(null)
  const pageDomain = axes?.page ? values[axes.page] || [] : []
  const page = axes?.page ? (pageValue && pageDomain.includes(pageValue) ? pageValue : pageDomain[0] ?? null) : null

  const [paint, setPaint] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [onlyGaps, setOnlyGaps] = useState(false)
  const [rowLimit, setRowLimit] = useState(GRID_VIEW_ROW_STEP)
  const [colPage, setColPage] = useState(0)
  useEffect(() => {
    if (!focus) return
    setQ(focus.q || '')
    setOnlyGaps(!!focus.onlyGaps)
    if (focus.onlyGaps) setPaint(null)
    setRowLimit(GRID_VIEW_ROW_STEP)
  }, [focus?.nonce]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── the page of rows / columns (server in tenant mode) ──────────────────
  const [cov, setCov] = useState<UdaCoverage | null>(null)
  const [loaded, setLoaded] = useState<string[]>([])
  const [covError, setCovError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)
  const bodyKey = mode === 'tenant' ? JSON.stringify(tenantBodyOf(draft.layer, draft.design)) : JSON.stringify(form)
  const wantRows = q.trim() ? Number.MAX_SAFE_INTEGER : rowLimit

  useEffect(() => {
    if (!axes || !form || !ready) return undefined
    const my = ++seq.current
    const run = async () => {
      setLoading(true)
      try {
        const rows: string[] = []
        let first: UdaCoverage | null = null
        let offset = 0
        for (;;) {
          const req = {
            form_no: formNo, rows_axis: axes.rows, cols_axis: axes.cols, page_value: page,
            row_offset: offset, row_limit: GRID_ROW_PAGE, col_offset: colPage * GRID_VIEW_COLS,
            col_limit: GRID_VIEW_COLS, only_gaps: onlyGaps,
          }
          const r = mode === 'tenant'
            ? await transport.coverage(objName, { ...req, body: JSON.parse(bodyKey) })
            : localCoverage(form, values, req)
          if (my !== seq.current) return
          if (!first) first = r
          rows.push(...r.rows)
          offset += GRID_ROW_PAGE
          if (rows.length >= Math.min(wantRows, r.row_total) || !r.rows.length) break
        }
        setCov(first)
        setLoaded(rows)
        setCovError(null)
      } catch (err) {
        console.warn('[uda setup] the grid page could not be read', err)
        if (my === seq.current) setCovError((err as Error)?.message || 'The grid could not be read')
      } finally {
        if (my === seq.current) setLoading(false)
      }
    }
    const t = setTimeout(() => { void run() }, 250)
    return () => clearTimeout(t)
  }, [axes?.rows, axes?.cols, page, colPage, onlyGaps, bodyKey, wantRows, ready, formNo, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── painting ─────────────────────────────────────────────────────────────
  const drag = useRef<'add' | 'remove' | null>(null)
  useEffect(() => {
    const up = () => { drag.current = null }
    window.addEventListener('mouseup', up)
    return () => window.removeEventListener('mouseup', up)
  }, [])
  const activeSet = paint ? form?.sets.find(s => s.key === paint) || null : null
  const setLocked = useCallback((key: string) => {
    const s = form?.sets.find(x => x.key === key)
    if (!s || mode === 'design' || s.origin !== 'design') return false
    return !(s.tenant_may || []).includes('rules')
  }, [form, mode])
  const applyPaint = (row: string, col: string | null, add: boolean) => {
    if (!paint || !axes) return
    const combo = cellCombo(axes, row, col, page)
    draft.edit(opSet(formNo, paint, (s, f) => { s.rules = paintCell(s.rules, combo, f.drivers, add) }))
  }
  const toggleHeading = (along: 'row' | 'col', v: string) => {
    if (!paint || !axes) return
    if (setLocked(paint)) { notify('info', 'Who gets this standard set is fixed by the application.'); return }
    const others = along === 'row' ? (axes.cols ? values[axes.cols] || [] : []) : values[axes.rows] || []
    draft.edit(opSet(formNo, paint, (s, f) => { s.rules = toggleSlice(s.rules, axes, along, v, others, page, f.drivers) }))
  }

  if (!form || !axes) return <div className="uds-na">Choose the deciding columns first.</div>
  const rd = drivers.find(d => d.key === axes.rows)!
  const cd = axes.cols ? drivers.find(d => d.key === axes.cols) || null : null
  const pd = axes.page ? drivers.find(d => d.key === axes.page) || null : null
  const countOf = (key: string, code: string) => {
    const d = drivers.find(x => x.key === key)
    return d ? domains.domainOf(formNo, d).values.find(v => v[0] === code)?.[2] ?? null : null
  }
  const needle = q.trim().toLowerCase()
  const matching = needle
    ? loaded.filter(r => r.toLowerCase().includes(needle) || lab(axes.rows, r).toLowerCase().includes(needle))
    : loaded
  const rowTotal = needle ? matching.length : (cov?.row_total ?? 0)
  const rows = matching.slice(0, rowLimit)
  const cols: Array<string | null> = cd ? (cov?.cols || []) : [null]
  const colTotal = cov?.col_total ?? 0
  const sets = (form.sets || []).filter(s => !s.hidden)
  const keys = drivers.map(d => d.key)

  return (
    <div {...tw('screen', 'uda-grid')}>
      <div className="uds-bar">
        <span className="uds-lbl" style={{ margin: 0 }}>Paint with</span>
        <div className="uds-chips">
          {sets.map(s => (
            <button key={s.key} type="button" className={`uds-vchip ${paint === s.key ? 'on' : ''}`}
              disabled={setLocked(s.key)} title={setLocked(s.key) ? 'Who gets this standard set is fixed by the application' : undefined}
              onClick={() => setPaint(p => (p === s.key ? null : s.key))} {...tw('action', `uda-paint:${s.key}`)}>
              <SetDot color={s.color} />{s.label || s.key}
            </button>
          ))}
        </div>
        {paint && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPaint(null)}>Stop painting</button>}
      </div>
      <div className="uds-hint" style={{ margin: '0 0 10px' }}>
        {activeSet
          ? <>Click or drag across cells to add or remove <b>{activeSet.label || activeSet.key}</b>. Click a row or column heading to switch the whole row or column.</>
          : 'Pick a set above to paint, or click a cell to see and change which sets apply there.'}
      </div>
      <div className="uds-bar">
        <input className="uds-inp sm" style={{ flex: '1 1 220px', maxWidth: 320 }} placeholder={`Find ${driverLabel(rd).toLowerCase()}`}
          value={q} aria-label="Find a row" onChange={e => { setQ(e.target.value); setRowLimit(GRID_VIEW_ROW_STEP) }} />
        <label className={`uds-vchip ${onlyGaps ? 'on' : ''}`}>
          <input type="checkbox" checked={onlyGaps} style={{ margin: 0 }} onChange={() => { setOnlyGaps(v => !v); setRowLimit(GRID_VIEW_ROW_STEP) }} />
          Only rows with gaps
        </label>
        {drivers.length > 1 && (
          drivers.length === 2 ? (
            <button type="button" className="btn btn-secondary btn-sm" title="Swap rows and columns"
              onClick={() => setOverride({ rows: axes.cols || axes.rows, cols: axes.rows })}>
              <ArrowLeftRight size={14} /> Swap rows and columns
            </button>
          ) : (
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
              Rows
              <select className="uds-inp sm" style={{ width: 'auto' }} value={axes.rows}
                onChange={e => { const r = e.target.value; setOverride({ rows: r, cols: r === axes.cols ? axes.rows : axes.cols }); setColPage(0) }}>
                {drivers.map(d => <option key={d.key} value={d.key}>{driverLabel(d)}</option>)}
              </select>
              Columns
              <select className="uds-inp sm" style={{ width: 'auto' }} value={axes.cols || ''}
                onChange={e => { const c = e.target.value; setOverride({ rows: c === axes.rows ? (axes.cols || axes.rows) : axes.rows, cols: c }); setColPage(0) }}>
                {drivers.map(d => <option key={d.key} value={d.key}>{driverLabel(d)}</option>)}
              </select>
            </span>
          )
        )}
        {cd && colTotal > GRID_VIEW_COLS && (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 13, color: 'var(--color-text-secondary)' }}>
            <button type="button" className="uds-icbtn" aria-label="Previous columns" disabled={!colPage} onClick={() => setColPage(p => Math.max(0, p - 1))}><ChevronLeft size={15} /></button>
            {driverLabel(cd)} {colPage * GRID_VIEW_COLS + 1}–{Math.min(colTotal, colPage * GRID_VIEW_COLS + GRID_VIEW_COLS)} of {colTotal}
            <button type="button" className="uds-icbtn" aria-label="Next columns" disabled={(colPage + 1) * GRID_VIEW_COLS >= colTotal} onClick={() => setColPage(p => p + 1)}><ChevronRight size={15} /></button>
          </span>
        )}
        {pd && (
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
            {driverLabel(pd)}
            <select className="uds-inp sm" style={{ width: 'auto' }} value={page || ''} onChange={e => setPageValue(e.target.value)}
              {...tw('field', 'uda-grid-page')}>
              {pageDomain.map(v => <option key={v} value={v}>{lab(pd.key, v)}</option>)}
            </select>
          </label>
        )}
        {loading && <span className="uds-spin" aria-label="Reading" />}
      </div>
      {covError && <div className="uds-hint" style={{ color: 'var(--color-error)', marginBottom: 8 }}>{covError}</div>}
      <div className="uds-gridwrap">
        <table className="uds-agrid">
          <thead>
            <tr>
              <th className="corner">{driverLabel(rd)} \ {cd ? driverLabel(cd) : ''}</th>
              {cols.map(cv => (
                <th key={String(cv)}>
                  {cd && cv !== null ? (
                    <button type="button" className="uds-ghead" disabled={!paint} onClick={() => toggleHeading('col', cv)}
                      {...tw('action', `uda-col:${cv}`)}>
                      {lab(cd.key, cv)}<span className="uds-gn">{(countOf(cd.key, cv) ?? '').toLocaleString()}</span>
                    </button>
                  ) : <span style={{ display: 'block', padding: '8px 10px' }}>Attribute sets</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(rv => (
              <tr key={rv}>
                <th>
                  <button type="button" className="uds-ghead row" disabled={!paint} onClick={() => toggleHeading('row', rv)}
                    {...tw('action', `uda-row:${rv}`)}>
                    <span>{lab(rd.key, rv)}</span><span className="uds-gn">{(countOf(rd.key, rv) ?? '').toLocaleString()}</span>
                  </button>
                </th>
                {cols.map(cv => {
                  const combo = cellCombo(axes, rv, cv, page)
                  const hit = cellSets(sets, combo, keys)
                  const hitSets = hit.map(k => sets.find(s => s.key === k)!).filter(Boolean)
                  const mine = !!activeSet && setCovers(activeSet, combo, keys)
                  return (
                    <td key={String(cv)}>
                      <button type="button" className={`uds-gcell ${hit.length ? '' : 'empty'} ${mine ? 'mine' : ''}`}
                        aria-label={`${lab(rd.key, rv)}${cd && cv !== null ? ` and ${lab(cd.key, cv)}` : ''}: ${hitSets.length ? hitSets.map(s => s.label || s.key).join(', ') : 'no attribute set'}`}
                        {...tw('action', `uda-cell:${rv}:${cv ?? ''}`)}
                        onMouseDown={e => {
                          if (!paint || e.button !== 0) return
                          e.preventDefault()
                          drag.current = mine ? 'remove' : 'add'
                          applyPaint(rv, cv, drag.current === 'add')
                        }}
                        onMouseEnter={() => {
                          if (!paint || !drag.current) return
                          if (drag.current === 'add' && !mine) applyPaint(rv, cv, true)
                          else if (drag.current === 'remove' && mine) applyPaint(rv, cv, false)
                        }}
                        onKeyDown={e => {
                          if (paint && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); applyPaint(rv, cv, !mine) }
                        }}
                        onClick={() => { if (!paint) openDialog({ kind: 'cell', combo }) }}>
                        {hitSets.length ? (
                          <>
                            {hitSets.slice(0, 2).map(s => (
                              <span key={s.key} className="uds-gb"><SetDot color={s.color} size={8} /><span>{s.label || s.key}</span></span>
                            ))}
                            {hitSets.length > 2 && <span className="uds-gmore">+{hitSets.length - 2}</span>}
                          </>
                        ) : <span className="uds-gnone">None</span>}
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={cols.length + 1} className="uds-na" style={{ padding: 16 }}>
                {loading || !ready ? 'Reading…' : onlyGaps ? 'No row has a gap.' : 'No rows match.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, fontSize: 13, color: 'var(--color-text-secondary)' }}>
        Showing {rows.length} of {rowTotal} {driverLabel(rd).toLowerCase()} rows
        {rows.length < rowTotal && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRowLimit(n => n + GRID_VIEW_ROW_STEP)}>
            Show {GRID_VIEW_ROW_STEP} more
          </button>
        )}
      </div>
    </div>
  )
}
